import axiosInstance from "./axios-instance";

export const storeFile = async (storeId: string, fileName: string, buffer: Buffer, fileStoreUrl: string) => {
    try {
        const normalizedFileName = fileName || `upload-${Date.now()}.pdf`;
        const contentType = normalizedFileName.toLowerCase().endsWith(".pdf")
            ? "application/pdf"
            : "application/octet-stream";

        if (!fileStoreUrl.includes("localhost")) {
            const presignResponse = await generatePresignedUrl({
                storeId,
                fileName: normalizedFileName,
                method: "PUT",
                contentType,
                preserveFileName: false,
            });

            const body = buffer as unknown as Uint8Array;
            const response = await fetch(presignResponse.url, {
                method: presignResponse.method || "PUT",
                headers: {
                    "Content-Type": contentType,
                    "Content-Disposition": `attachment; filename="${presignResponse.fileName || normalizedFileName}"`,
                },
                body: body as unknown as BodyInit,
            });

            if (!response.ok) {
                throw new Error(`Failed to upload file via presigned URL. Status: ${response.status}`);
            }

            return {
                fileName: presignResponse.fileName || normalizedFileName,
                key: presignResponse.key,
            };
        }

        const response = await axiosInstance.post<any>('/file-store/storeFile', { 
            storeId, 
            fileName: normalizedFileName,
            pdfBuffer: buffer,
            fileStoreUrl,    
         })
        return response.data;
    } catch (error) {
        console.error("Error with call lambda: ", error);
        throw error;
    }
}

type PresignMethod = 'PUT' | 'GET';

interface GeneratePresignedUrlPayload {
    storeId: string;
    fileName: string;
    method?: PresignMethod;
    contentType?: string;
    expiresIn?: number;
    preserveFileName?: boolean;
    responseContentDisposition?: string;
    responseContentType?: string;
}

interface GeneratePresignedUrlResponse {
    url: string;
    key: string;
    fileName?: string;
    method: PresignMethod;
    expiresIn: number;
}

export const generatePresignedUrl = async (
    payload: GeneratePresignedUrlPayload
): Promise<GeneratePresignedUrlResponse> => {
    try {
        const response = await axiosInstance.post<GeneratePresignedUrlResponse>(
            '/file-store/generatePresignedUrl',
            payload
        );
        return response.data;
    } catch (error) {
        console.error('Error generating presigned URL: ', error);
        throw error;
    }
};

export const openStoredPdf = async (storeId: string, fileName: string, fileStoreUrl: string) => {
    if (!storeId || !fileName) {
        throw new Error('storeId and fileName are required');
    }

    if (!fileStoreUrl.includes("localhost")) {
        const presignResponse = await generatePresignedUrl({
            storeId,
            fileName,
            method: 'GET',
            preserveFileName: false,
            responseContentDisposition: 'inline',
            responseContentType: 'application/pdf',
        });

        window.open(presignResponse.url, '_blank');
        return;
    }

    const response = await retrieveFile(storeId, fileName, fileStoreUrl);
    const buffer = Buffer.from(response, 'base64');
    const blob = new Blob([buffer], {
        type: 'application/pdf',
    });
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
};

export const retrieveFile = async (storeId: string, fileName: string, fileStoreUrl: string) => {
    try {
        const response = await axiosInstance.post<any>('/file-store/retrieveFile', { 
            storeId, 
            fileName,
            fileStoreUrl,    
         })
        return response.data;
    } catch(error){
        throw error;
    }
}

const normalizePdfLink = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    let normalized = value.trim();
    if (!normalized) {
        return null;
    }

    try {
        if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
            normalized = new URL(normalized).pathname || normalized;
        }
    } catch {
        // Keep original text when URL parsing fails and normalize via path splitting below.
    }

    normalized = normalized.split('#')[0].split('?')[0].trim();
    const segments = normalized.split('/').filter(Boolean);
    const fileName = segments[segments.length - 1] || '';
    return fileName || null;
};

const MERGE_PDF_NONE_FOUND_MESSAGE = 'None of the requested PDFs were found';
const FILE_STORE_MISSING_MESSAGE = 'Requested file was not found';

const readMergePdfErrorMessage = (error: any): string => {
    const message = error?.response?.data?.message;
    if (typeof message === 'string') {
        return message;
    }

    const nestedMessage = error?.response?.data?.error?.message;
    if (typeof nestedMessage === 'string') {
        return nestedMessage;
    }

    return '';
};

export const isMergePdfNoneFoundError = (error: any): boolean => {
    const status = error?.response?.status;
    const message = readMergePdfErrorMessage(error);

    return status === 404 && message.includes(MERGE_PDF_NONE_FOUND_MESSAGE);
};

const readFileStoreErrorMessage = (error: any): string => {
    const message = error?.response?.data?.message;
    if (typeof message === 'string') {
        return message;
    }

    const nestedMessage = error?.response?.data?.error?.message;
    if (typeof nestedMessage === 'string') {
        return nestedMessage;
    }

    return '';
};

export const isStoredFileMissingError = (error: any): boolean => {
    const status = error?.response?.status;
    const message = readFileStoreErrorMessage(error);

    return status === 404 && message.includes(FILE_STORE_MISSING_MESSAGE);
};

export const mergePdfFiles = async (links: any[], storeId: string) => {
    try {
        const normalizedLinks = Array.isArray(links)
            ? links.map(normalizePdfLink).filter((link): link is string => Boolean(link))
            : [];

        const response = await axiosInstance.post<any>('/file-store/mergeFile', { 
            pdfLinks: normalizedLinks, 
            storeId,
         })
        return response.data;
    } catch(error){
        throw error;
    }
}

export const getMergedPdfPrintUrl = async (response: any): Promise<string> => {
    if (response && typeof response === 'object' && typeof response.url === 'string') {
        const pdfResponse = await fetch(response.url);
        if (!pdfResponse.ok) {
            throw new Error(`Failed to load merged PDF. Status: ${pdfResponse.status}`);
        }

        const pdfBlob = await pdfResponse.blob();
        return URL.createObjectURL(
            pdfBlob.type ? pdfBlob : new Blob([pdfBlob], { type: 'application/pdf' })
        );
    }

    const pdfData = typeof response === 'string' ? response : response?.pdfData;
    const buffer = Buffer.from(pdfData || '', 'base64');
    const blob = new Blob([buffer], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
}
